import _ from "lodash";
import React, { useEffect, useState } from "react";
import queryString from "query-string";
import { Box, Flex } from "@rebass/grid";
import { useLocation, useSearchParams } from "react-router-dom";
import logo from "assets/ampd-red-black.svg";
import useLogin from "Common/hooks/useLogin";
import { LogInRequest } from "Common/proto/entity/entityService_pb";
import { SiteSelector } from "Common/proto/entity/site_pb";
import * as proto from "Common/utils/proto";
import { configureLock } from "./login/auth";
import Styles from "./login/styles";
import Immutable from "immutable";
import {
  extractErrorMessage,
  extractErrorRequestID
} from "Common/errors/error";
import sanitizeHtml from "sanitize-html";
import { Message } from "semantic-ui-react";
import { EmailSupportButton } from "../components/ErrorModal";
import styled from "styled-components";

// Some of the predefined messages from the Auth0 reset password flow are
// awkward, so we rewrite them if possible.
const messageMap = Immutable.Map({
  "you can now login to the application with the new password.":
    "You can now log in with your new password.",
  "your email was verified. you can continue using the application.":
    "Your email was verified.<br />You can now log in to Ampd."
});

const LOGIN_BANNER_TEXT = process.env.REACT_APP_LOGIN_BANNER_TEXT;
const LoginPageBanner = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  background-color: rgb(244 32 26);
  z-index: 9999;
  text-align: center;
  padding: 1em 7em;
`;

// Key and value used in the query parameters to indicate there was an error
// from Ampd during login.
const logInErrorKey = "logInError";
const logInErrorTrue = "true";

// Key used in the query parameters with the request ID of the failed login
// request, if known.
const logInRequestIdKey = "logInRequestId";

function Login() {
  return (
    <>
      <LoginContent />
    </>
  );
}

function LoginContent() {
  const [logIn, { loading, called, error }] = useLogin(
    process.env.REACT_APP_AUTH_LOGIN_URI
  );

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const referrer = sessionStorage.getItem("login_ref");
  const site = searchParams.get("site");

  // If we receive ?tab=signup, show the sign up tab.
  const initialScreen =
    searchParams.get("tab") === "signup" ? "signUp" : "login";

  // The success and message parameters are set by Auth0 during redirect after
  // the password recovery process. It's not advertised, but Auth0 also sets
  // the email query parameter.
  //
  // https://auth0.com/docs/auth0-email-services/customize-email-templates#configuring-the-redirect-to-url
  const [email, setEmail] = useState(searchParams.get("email"));
  const success = searchParams.get("success");

  const messageParam = searchParams.get("message");
  const message = messageMap.get(_.toLower(messageParam), messageParam);

  // Set by us if there is an error during the Ampd login process, after Auth0
  // authentication has completed.
  const hasLogInError = searchParams.get(logInErrorKey) === logInErrorTrue;
  const logInRequestId = searchParams.get(logInRequestIdKey);

  // If there is an error, log the error, add a generic error message and
  // redirect back to the login screen.
  useEffect(() => {
    if (!error) {
      return;
    }

    console.error(extractErrorMessage(error)); // log the error to datadog

    // Setting logInError=true triggers the LogInErrorMessage to display.
    const params = queryString.parse(location.search);
    params[logInErrorKey] = logInErrorTrue;

    const requestId = extractErrorRequestID(error);
    if (requestId) {
      params[logInRequestIdKey] = requestId;
    }

    // If we have an email, e.g., from the "authenticated" event below, include
    // that in the URL so we can pre-populate the value on refresh.
    if (email) {
      params["email"] = email;
    }

    window.location.href = `${location.pathname}?${queryString.stringify(
      params
    )}`;
  }, [email, location, error]);

  useEffect(() => {
    const lock = configureLock({ initialScreen, email });

    const hashParams = queryString.parse(window.location.hash);

    // The standard way of interacting with the lock widget is by listening to
    // events that it emits. This seems to work fine for the "authenticated"
    // event, though we had problems with the "authorization_error" event
    // (see below).
    //
    // One case where we absolutely need the "authenticated" event, and cannot
    // handle a login through the hash parameters, is if the user is already
    // logged in to Auth0 and does not require a social/password login. In that
    // case there is no redirection and no hash parameter; all that we have is
    // this emitted event. (We could disable this by setting rememberLastLogin
    // to false, but it's probably more useful than not right now.)
    lock.on("authenticated", authResult => {
      // Set the email in case the login fails so we can populate that when we
      // reload the page.
      const authEmail = _.get(authResult, "idTokenPayload.email");
      if (authEmail) {
        setEmail(authEmail);
      }

      const loginReq = proto.set(new LogInRequest(), {
        authentication: proto.set(new LogInRequest.Authentication(), {
          auth0: proto.set(new LogInRequest.Auth0(), {
            id_token: authResult.idToken
          })
        })
      });

      // If there is a site specified, include it in the request. The log in
      // is rejected if the user is not a member of the site, except in the
      // case of superusers, in which case they are added as a provisional
      // member.
      if (site) {
        loginReq.setSiteSelector(
          proto.set(new SiteSelector(), {
            site_alias: site
          })
        );
      }

      logIn(loginReq);
    });

    if (message) {
      lock.show({
        flashMessage: {
          type: success === "false" ? "error" : "success",
          text: sanitizeHtml(message)
        }
      });
    }

    // For some reason, the lock.on("authorization_error") event is not
    // consistently handled, possibly because of the history replacement logic
    // above, but that was not conclusively confirmed.
    //
    // On the other hand, we seem to hit this block consistently if we receive
    // an error, e.g., if a user's email has not been verified.
    if (hashParams.error) {
      // If there is an error, show that.
      lock.show({
        flashMessage: {
          type: "error",
          text: hashParams.error_description || "Unknown error."
        }
      });
    } else if (!hashParams.id_token) {
      // If there was no other special handling, show the login widget.
      lock.show();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (called && !loading && !error) {
    if (referrer) {
      window.location.href = referrer;
    } else {
      window.location.href = "/";
    }

    // Clean up storage items that should not longer be needed.
    sessionStorage.removeItem("login_ref", "");
  }

  return (
    <>
      {LOGIN_BANNER_TEXT && LOGIN_BANNER_TEXT.length > 0 && (
        <LoginPageBanner>
          <h4 style={{ height: "100%" }}>{LOGIN_BANNER_TEXT}</h4>
        </LoginPageBanner>
      )}
      <Styles.Container id="signup" flexDirection="column" flex="1 1 auto">
        <Flex justifyContent="center">
          <Flex flexDirection="column" alignItems="center">
            <Styles.Main flexDirection="column">
              <Flex as="header" justifyContent="center">
                <Styles.Logo alignItems="center" justifyContent="center">
                  <img src={logo} alt="Ampd Logo" />
                </Styles.Logo>
              </Flex>

              <Flex flex="auto" id="auth0-container" />
            </Styles.Main>

            {hasLogInError && (
              <Flex flex="auto" id="error">
                <LogInErrorMessage email={email} requestId={logInRequestId} />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Styles.Container>
      <Styles.Dimmer />
    </>
  );
}

function LogInErrorMessage(props) {
  const { email, requestId } = props;

  const emailInfo = {
    subject: "I am having trouble logging into my Ampd account",
    body: `I received an error while logging into my Google Ads account. Please assist me.

Email: ${email}

Request ID: ${requestId}

URL: ${window.location.href}`
  };

  return (
    <>
      <Message error>
        <Flex flexDirection="column" alignItems="center">
          <p style={{ margin: "1rem" }}>
            There was an error logging in to Ampd. Please contact us for
            assistance.
          </p>
          <Flex flexDirection="row">
            <Box m={"1rem"}>
              <EmailSupportButton
                primary
                style={{ whiteSpace: "nowrap" }}
                emailInfo={emailInfo}
              />
            </Box>
          </Flex>
        </Flex>
      </Message>
    </>
  );
}

export default Login;
